import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls, textareaCls } from "./form-controls";

export interface ContractorFormValues {
  _id?: string;
  name: string;
  serviceType: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

// Create / edit form for a single contractor. Pass `existing` to put
// the dialog in edit mode (pre-fills + calls update); omit it to
// create a new one.
export function ContractorFormDialog({
  open,
  onClose,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  existing?: ContractorFormValues;
}) {
  const [name, setName] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(existing?.name ?? "");
    setServiceType(existing?.serviceType ?? "");
    setEmail(existing?.email ?? "");
    setPhone(existing?.phone ?? "");
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [open, existing?._id]);

  const create = useMutation(anyApi.contractors.create);
  const update = useMutation(anyApi.contractors.update);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name,
        serviceType,
        email: email || undefined,
        phone: phone || undefined,
        notes: notes || undefined,
      };
      if (existing?._id) {
        await update({ contractorId: existing._id, ...payload });
      } else {
        await create(payload);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={existing ? "Edit contractor" : "Add contractor"}
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            placeholder="Northwest Furnace Co."
            className={inputCls}
          />
        </Field>
        <Field label="Service type" required>
          <input
            type="text"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            required
            placeholder="Furnace, Plumbing, Electrical, …"
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="dispatch@example.com"
              className={inputCls}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(503) 555-0142"
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Preferred days, gate code, anything to remember"
            className={`${textareaCls} resize-none`}
          />
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || !name.trim() || !serviceType.trim()}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {submitting
              ? "Saving..."
              : existing
                ? "Save changes"
                : "Add contractor"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
